import publicSite from '../config/publicSite';

const { ORIGIN, publicPath, getStructuredData } = publicSite;

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, String(value)));
}

export function clearPublicSeo() {
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
  document.getElementById('noesa-structured-data')?.remove();
  upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, follow' });
}

// One source for client navigation and build-time HTML: metadata, URLs and entities.
export function applyPublicSeo(page) {
  if (!page) return;
  const language = page.language || 'ru';
  const canonical = new URL(page.path, ORIGIN).toString();
  const image = new URL(page.ogImage || '/icon-512.png', ORIGIN).toString();
  document.documentElement.lang = language;
  document.title = page.title;
  upsertMeta('meta[name="description"]', { name: 'description', content: page.description });
  upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index, follow' });
  for (const [name, content] of Object.entries({
    'og:title': page.title, 'og:description': page.description, 'og:url': canonical,
    'og:type': page.ogType || 'website', 'og:image': image,
    'og:locale': language === 'en' ? 'en_US' : 'ru_RU',
    'og:locale:alternate': language === 'en' ? 'ru_RU' : 'en_US',
  })) upsertMeta(`meta[property="${name}"]`, { property: name, content });
  for (const [name, content] of Object.entries({
    'twitter:card': 'summary_large_image', 'twitter:title': page.title,
    'twitter:description': page.description, 'twitter:image': image,
  })) upsertMeta(`meta[name="${name}"]`, { name, content });
  upsertLink('link[rel="canonical"]', { rel: 'canonical', href: canonical });
  document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((link) => link.remove());
  for (const lang of ['ru', 'en', 'x-default']) {
    upsertLink(`link[rel="alternate"][hreflang="${lang}"]`, {
      rel: 'alternate', hreflang: lang,
      href: new URL(publicPath(page.path, lang === 'en' ? 'en' : 'ru'), ORIGIN).toString(),
    });
  }
  let schema = document.getElementById('noesa-structured-data');
  if (!schema) {
    schema = document.createElement('script');
    schema.id = 'noesa-structured-data';
    schema.type = 'application/ld+json';
    document.head.appendChild(schema);
  }
  schema.textContent = JSON.stringify(getStructuredData(page));
}

export function applyNoIndexSeo({ title, description, canonicalPath }) {
  try {
    clearPublicSeo();
    const canonical = new URL(canonicalPath, ORIGIN).toString();
    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, follow' });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
    document.getElementById('noesa-structured-data')?.remove();
  } catch {}
}
